<template>
  <page-with-aside :hasFooter="true">
    <header class="page-header">
      <!-- already logged in so hide login button -->
      <nav>
        <router-link to="/about" class="button">About the Program</router-link>
      </nav>
    </header>

    <template #content>
      <header class="content-header">
        <h2>University of Michigan</h2>
        <h2>Consent to be part of a Research Study</h2>
        <div class="header-box">NAME OF STUDY AND RESEARCHERS</div>

        <p>
          <b>
            Title of Project: Patient-centered development of the Cancer Support
            Community's Open to Options&reg; Chatbot Program cancer treatment
            support in partnership with the Cancer Support Community
          </b>
        </p>

        <p><b>Principal Investigator: Sarah T. Hawley, PhD, MPH</b></p>
      </header>

      <section class="content">
        <div class="consent-box">
          <div class="header-box">GENERAL INFORMATION</div>

          <p>
            We're doing a study to learn more about the feasibility of the Open
            to Options Chatbot Program. Open to Options is an already existing
            platform that helps patients prepare a list of questions and
            concerns, and prioritize to what the patient feels is the most
            important to communicate with their doctor in their upcoming visit.
            We have developed a Chatbot interface to help guide patients through
            the Open to Options program. To get information, we'd like 40 people
            to use the Chatbot, and answer a survey. Eligible participants for
            this study have a new cancer diagnosis, are being seen at the Rogel
            Cancer Center are 18 years old or older and have access to the
            internet. We expect it to take about 45 minutes to complete the
            survey and interact with the Chatbot.
          </p>

          <p>
            Taking part in this study is voluntary. You don't have to answer it
            if you'd rather not. You can skip any questions that you don't want
            to answer, whatever the reason, and you don't have to tell us why.
            Choosing not to answer our survey or use the Chatbot won't affect
            the medical care you might receive at the University of Michigan
            Health System.
          </p>

          <p>
            If you choose to participate in this study, you will be randomized
            to take either a pre-survey and Chatbot or Chatbot and post-survey.
            Regardless of when you take the survey, your experience with the
            Chatbot will not be changed. The Chatbot program will give you a
            brief introduction, and then take you through a series of sections.
            Once completed you will have the opportunity to review and edit all
            sections before producing a summary.
          </p>

          <p>
            You may receive a reminder email during the study if you leave the
            Chatbot program without completing the sections. You may also
            receive a reminder email to take the post-survey if that is how you
            were randomized. You will not receive more than one email reminder
            for a given event, and you will not receive a reminder email for
            simply skipping a question. Once you complete the Chatbot and survey
            your participation is complete.
          </p>

          <p>
            It's possible that some of the questions may make you feel
            uncomfortable. If a question makes you uncomfortable, you can just
            skip it and go to the next question.
          </p>

          <p>
            To keep your information confidential, we will label your survey
            with a code, rather than your name or any other details that someone
            could use to identify you. Although we'll keep a list of all the
            people who answered our survey, no one outside our study team will
            be able to figure out who answered the survey or which people gave
            which answers. We plan to publish what we learn from this study, but
            we won't include any personal information that could reveal who
            answered the survey. Answering our survey won't benefit you
            directly. We hope what we learn will help other people in the
            future.
          </p>

          <p>
            If you decide to stop participating in the study before the study is
            over, we will keep any data we have collected so far, but remove all
            identifying information.
          </p>

          <p>
            Your collected information may be shared with the Cancer Support
            Community.
          </p>

          <p>
            With appropriate permissions, your collected information may also be
            shared with other researchers, here, around the world, and with
            companies.
          </p>

          <p>
            Your identifiable private information may be stripped of identifiers
            and used for future research studies or distributed to another
            researcher for future research studies without additional informed
            consent.
          </p>

          <p>
            Research can lead to new discoveries, such as new tests, drugs, or
            devices. Researchers, their organizations, and other entities,
            including companies, may potentially benefit from the use of the
            data or discoveries. You will not have rights to these discoveries
            or any proceeds from them.
          </p>

          <p>
            To thank you for taking part in our study, we'll send you a $10
            eGift card after you take the survey. The University of Michigan
            accounting department may need your name, address, Social Security
            number, payment amount, and related information for tax reporting
            purposes.
          </p>

          <p>
            A description of this clinical trial will be available on
            www.ClinicalTrials.gov. This website will not include information
            that can identify you. At most, the website will include a summary
            of the results. You can search this website at any time.
          </p>

          <div class="header-box">CONTACT INFORMATION</div>

          <p>
            <b>
              To find out more about the study, to ask a question or express a
              concern about the study, or to talk about any problems you may
              have as a study subject, you may contact one of the following:
            </b>
          </p>

          <p>
            Principal Investigator: Sarah T. Hawley, PhD, MPH<br />
            Mailing Address: 2800 Plymouth Rd, NCRC Bldg 16<br />
            Email: sarahawl@med.umich.edu
          </p>

          <p>
            Study Coordinator: Aaron Rankin<br />
            Mailing Address: 2800 Plymouth Rd, NCRC Bldg 16<br />
            Email: rankina@med.umich.edu
          </p>

          <p>
            <b
              >You may also express a concern about a study by contacting the
              Institutional Review Board:</b
            >
          </p>

          <p>
            University of Michigan Medical School Institutional Review Board
            (IRBMED)<br />
            2800 Plymouth Road<br />
            Building 520, Room 3214<br />
            Ann Arbor, MI 48109-2800<br />
            734-763-4768<br />
            E-mail: irbmed@umich.edu
          </p>

          <p>
            If you are concerned about a possible violation of your privacy or
            concerned about a study, you may contact the University of Michigan
            Health System Compliance Help Line at 1-866-990-0111.
          </p>
        </div>
        <!-- End of Consent Content -->

        <b>Do you agree to take part in this study as described?</b>
        <form ref="form" @submit="handleSubmit">
          <ul
            v-for="(error, errorIndex) in errors"
            :key="'error_' + errorIndex"
          >
            <li v-html="error" />
          </ul>

          <ul
            v-for="(accessCodeError, accessCodeErrorIndex) in accessCodeErrors"
            :key="'accessCodeError_' + accessCodeErrorIndex"
          >
            <li v-html="accessCodeError" />
          </ul>

          <label>
            <input
              id="radio1"
              name="consent_status"
              type="radio"
              value="consented"
              required
            />
            <span>Yes</span>
          </label>
          <label>
            <input
              id="radio2"
              name="consent_status"
              type="radio"
              value="declined"
              required
            />
            <span>No</span>
          </label>
          <button type="submit">Continue</button>
        </form>

        <a :href="`${publicPath}consent.pdf`" target="_blank"
          >Consent form for your records</a
        >
      </section>
    </template>
  </page-with-aside>
</template>

<style scoped lang="scss">
@import "../../assets/scss/base/variables.scss";

h2 {
  margin-bottom: 0;
  text-align: center;
  @media (min-width: 450px) {
    margin: 0;
  }
}
h3 {
  font-family: "Karla", Helvetica Neue, Helvetica, Arial, sans-serif;
  color: $teal-main;
  margin-bottom: 0;
  font-weight: bold;
  margin-top: 0;
}
.header-box {
  background-color: #ccc;
  padding: 10px;
  border: 1px solid black;
  text-align: center;
  width: 100%;
  margin-bottom: 20px;
}
.consent-box {
  margin: 1rem 0 1rem;
  height: 55vh;
  overflow: scroll;
  border: 1px solid #9a9a9a;
  padding: 1rem;
  box-shadow: inset 0 2px 10px #ccc;
}
.form-group,
.form-group div {
  margin-top: 0.6rem;
}
form {
  // display: grid;
  // grid-template-columns: repeat(auto-fit, minmax(100px, 1fr));
  // grid-gap: 1rem;
  margin-bottom: 2rem;
  // align-items: end;

  ul {
    margin-bottom: 0;
  }
  label {
    display: flex;
    align-content: flex-start;
    padding-right: 2rem;
    input {
      margin: 0 1rem 0 0;
      flex: 0 0 16px;
    }
    input[type="radio"],
    input[type="checkbox"] {
      width: auto;
      margin-right: 0.5rem;
    }
  }
  button {
    margin-top: 0.5rem;
    grid-column: 1/3;
    justify-self: start;
  }
}
</style>


<script>
import PageWithAside from "@/components/PageWithAside.vue";
import { axios } from "@/main.js";

export default {
  name: "Consent",

  props: {
    accessCode: String,
  },

  data() {
    return {
      accessCodeErrors: null,
      errors: null,
      consentErrors: null,
      publicPath: process.env.BASE_URL,
    };
  },

  components: {
    PageWithAside,
  },
  methods: {
    clearErrors() {
      this.consentErrors = [];
    },

    handleSubmit(event) {
      event.preventDefault();

      this.sendConsentData();
    },

    sendConsentData() {
      var formData = new FormData(this.$refs.form);
      let self = this;

      this.clearErrors();
      let url = "/api/consent/" + self.accessCode + "/";

      axios
        .put(url, formData)
        .then(function (response) {
          let status = response.status;
          let formData = new FormData(self.$refs.form);
          let consent_status = formData.get("consent_status");

          switch (status) {
            case 200: // success
              if (consent_status == "consented") {
                self.$router.push("/accountsetup/" + self.accessCode);
              }

              if (consent_status == "declined") {
                self.$router.push("/noconsent");
              }
              break;

            default:
              break;
          }
        })
        .catch(function (error) {
          let status = error.response.status;
          switch (status) {
            case 400: // bad request
              self.errors = error.response.data;
              break;

            case 404: // not found
              self.accessCodeErrors = ["No such access code."];
              break;
          }
        });
    },

    goToChat() {
      this.$router.push("/chat/situation/new/");
      this.changeMode("chat");
    },
  },
};
</script>
